import React, { type FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useDescope } from '@descope/react-sdk';
import Logout from '@mui/icons-material/Logout';
import { useTheme } from '@mui/material';

import { getGetCurrentUserQueryKey, getGetUserByIdQueryKey, useGetCurrentUser, useUpdateUser } from '@/api/generated';
import LanguageSwitch from '@/components/LanguageSwitch/LanguageSwitch';
import { UserAvatar } from '@/components/UserAvatar';
import { useSnackbar } from '@/hooks/useSnackbar';
import { SETTINGS_STEP_KEYS, toUserSettings } from '@/root/linker';

import DropdownAvatarMenu from './DropdownAvatarMenu';

interface LanguageSwitchMenuProps {
  onChange?: (e: React.MouseEvent<HTMLElement>) => void;
}

const LanguageSwitchWithBackendSync: FC<LanguageSwitchMenuProps> = ({ onChange }) => {
  const { t, i18n } = useTranslation('common');
  const { showMessage } = useSnackbar();
  const queryClient = useQueryClient();
  const { data: currentUser } = useGetCurrentUser();
  const { mutateAsync: updateUser } = useUpdateUser();

  const handleLocaleChange = useCallback(
    async (locale: string) => {
      if (!currentUser?.id) return;
      try {
        await updateUser({
          userId: currentUser.id,
          data: { preferredLocale: locale }
        });
        await queryClient.invalidateQueries({ queryKey: getGetCurrentUserQueryKey() });
        await queryClient.invalidateQueries({ queryKey: getGetUserByIdQueryKey(currentUser.id) });
        await i18n.changeLanguage(locale);
      } catch {
        showMessage(t('common:error.somethingWentWrong'));
      }
    },
    [currentUser?.id, queryClient, showMessage, t, updateUser, i18n]
  );

  return <LanguageSwitch onChange={onChange} onLocaleChange={handleLocaleChange} />;
};

const MENU_ICON_SIZE = 2.5;
const MENU_PROFILE_PIC_SIZE = 3.25;

const ProfileMenu = () => {
  const { t } = useTranslation('common');
  const { spacing } = useTheme();
  const { logout } = useDescope();
  const navigate = useNavigate();

  const menuIconSize = parseFloat(spacing(MENU_ICON_SIZE));
  const menuProfilePictureSize = parseFloat(spacing(MENU_PROFILE_PIC_SIZE));

  const { data: currentUser } = useGetCurrentUser();
  const userId = currentUser?.id;

  const handleProfileClick = useCallback(() => {
    if (!userId) return;
    navigate(toUserSettings({ userId: userId, stepKey: SETTINGS_STEP_KEYS.BASIC_INFO }));
  }, [navigate, userId]);

  const items = useMemo(
    () => [
      {
        text: t('header.menu.items.profile'),
        Icon: () => <UserAvatar user={currentUser} size={menuIconSize} />,
        onClick: handleProfileClick
      },
      {
        isDivider: true
      },
      {
        Component: LanguageSwitchWithBackendSync
      },
      {
        isDivider: true
      },
      {
        text: t('header.menu.items.logout'),
        Icon: () => <Logout fontSize="small" />,
        onClick: () => logout()
      }
    ],
    [handleProfileClick, logout, menuIconSize, t, currentUser]
  );

  return (
    <DropdownAvatarMenu
      title={t('header.menu.title')}
      items={items}
      avatar={<UserAvatar user={currentUser} size={menuProfilePictureSize} />}
    />
  );
};

export default ProfileMenu;
